import { z } from 'zod';
import { AspectRatio } from '../enums';

/**
 * Образы (§12).
 *
 * Образ — сохранённый вариант внешнего вида пользователя, а НЕ его внешность:
 * `Look` описывает одежду и обстановку, `AppearanceAsset` — саму личность.
 * Смешивать нельзя: смена образа не должна портить узнаваемость лица.
 */

export const LookStatus = {
  DRAFT: 'draft',
  GENERATING: 'generating',
  READY: 'ready',
  FAILED: 'failed',
} as const;
export type LookStatus = (typeof LookStatus)[keyof typeof LookStatus];

export const lookStatusLabels: Record<LookStatus, string> = {
  draft: 'Черновик',
  generating: 'Создаётся',
  ready: 'Готов',
  failed: 'Не удалось',
};

/** Примеры из §12. Подставляются в поле названия как подсказки. */
export const lookPresets = [
  { name: 'Основной', prompt: 'нейтральный повседневный образ, студийный свет' },
  { name: 'Деловой', prompt: 'деловой костюм, офисная обстановка, мягкий свет' },
  { name: 'Casual', prompt: 'свитер и джинсы, светлая комната, дневной свет' },
  { name: 'Podcast', prompt: 'наушники и микрофон, тёплый приглушённый свет студии' },
  { name: 'Спортивный', prompt: 'спортивная форма, зал или улица, динамичный свет' },
  { name: 'Вечерний', prompt: 'вечерний образ, тёмный фон, контрастный свет' },
  { name: 'Футуристический', prompt: 'минималистичный технологичный образ, холодный свет' },
] as const;

export const createLookSchema = z
  .object({
    name: z.string().trim().min(1).max(60),
    /** §12.1: описание, референсы или их комбинация. */
    prompt: z.string().trim().max(500).optional(),
    referenceAssetIds: z.array(z.string().min(1)).max(5).default([]),
    /**
     * Место без людей, а не человек.
     *
     * Образ — «я в другой одежде и в другом месте», и человек в кадре есть
     * всегда. Локация — только место: она уходит фоном сцены с речью, где
     * человек уже свой. Смешивать их нельзя — в кадре окажется двое.
     */
    isLocation: z.boolean().default(false),
    /**
     * Формат кадра.
     *
     * Локация уходит фоном сцены, и фон обязан быть той же формы, что ролик:
     * вертикальная картинка в горизонтальном кадре либо обрежется, либо
     * оставит поля по краям — видно на первом же ролике 16:9.
     */
    aspectRatio: z
      .enum(Object.values(AspectRatio) as [AspectRatio, ...AspectRatio[]])
      .default('9:16'),
    /** Сколько вариантов создать за раз. */
    count: z.number().int().min(1).max(4).default(2),
  })
  .strict()
  .refine((value) => Boolean(value.prompt) || value.referenceAssetIds.length > 0, {
    message: 'Нужно описание или хотя бы один референс',
    path: ['prompt'],
  });
export type CreateLookInput = z.infer<typeof createLookSchema>;

/** §12.2: «создать ещё варианты» — с тем же или изменённым описанием. */
export const generateVariantsSchema = z
  .object({
    prompt: z.string().trim().max(500).optional(),
    count: z.number().int().min(1).max(4).default(2),
  })
  .strict();

export const updateLookSchema = z
  .object({
    name: z.string().trim().min(1).max(60).optional(),
    selectedVariantId: z.string().min(1).optional(),
    isPrimary: z.boolean().optional(),
  })
  .strict();

export interface LookVariantView {
  id: string;
  assetId: string;
  url: string | null;
  prompt: string | null;
  createdAt: string;
}

export interface LookView {
  id: string;
  name: string;
  status: LookStatus;
  statusLabel: string;
  prompt: string | null;
  isPrimary: boolean;
  /** Место без людей: годится фоном сцены, но не «образом». */
  isLocation: boolean;
  /** Формат кадра образа: фон обязан совпадать с форматом ролика. */
  aspectRatio: string;
  selectedVariantId: string | null;
  /** Превью выбранного варианта — то, что видно в сетке §61. */
  previewUrl: string | null;
  variants: LookVariantView[];
  errorCode: string | null;
  createdAt: string;
}
